import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBaaSAuth } from '@/contexts/BaaSAuthContext';
import { baasSupabase as supabase } from '@/integrations/supabase/baasClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Eye, EyeOff, Shield, Mail, Lock, Banknote, ArrowLeft } from 'lucide-react';
import { z } from 'zod';

type View = 'login' | 'forgot' | 'forgot-sent';

export default function BaaSLogin() {
  const savedEmail = localStorage.getItem('baas.rememberEmail') || '';
  const savedPassword = localStorage.getItem('baas.rememberPassword') || '';
  const savedRemember = localStorage.getItem('baas.rememberMe') === 'true';
  const [view, setView] = useState<View>('login');
  const [email, setEmail] = useState(savedEmail);
  const [password, setPassword] = useState(savedPassword);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [rememberMe, setRememberMe] = useState(savedRemember);
  const [alreadySent, setAlreadySent] = useState(false);
  const { signIn, user, baasUser, baasUserLoaded, setAuthGuard } = useBaaSAuth();
  const navigate = useNavigate();

  if (user && baasUserLoaded && baasUser) {
    navigate('/baas', { replace: true });
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    setAuthGuard(true);

    try {
      const { error: signInError, user: signedInUser } = await signIn(email, password);
      if (signInError) { setError('Invalid email or password'); setAuthGuard(false); setIsLoading(false); return; }

      const userId = signedInUser?.id;
      if (!userId) { setError('Authentication failed. Please try again.'); setAuthGuard(false); setIsLoading(false); return; }

      // Wait until the session token is fully propagated
      let sessionReady = false;
      for (let i = 0; i < 10; i++) {
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData?.session?.user?.id === userId) { sessionReady = true; break; }
        await new Promise(r => setTimeout(r, 300));
      }
      if (!sessionReady) { setError('Session could not be established. Please try again.'); setAuthGuard(false); setIsLoading(false); return; }

      // Verify BaaS portal membership
      const { data: membership } = await (supabase as any)
        .from('baas_portal_users')
        .select('id, status')
        .eq('user_id', userId)
        .eq('status', 'active')
        .maybeSingle();

      if (!membership) {
        setAuthGuard(false);
        await supabase.auth.signOut();
        setError('This account does not have BaaS Partner Portal access. Contact your administrator.');
        setIsLoading(false);
        return;
      }

      if (rememberMe) {
        localStorage.setItem('baas.rememberMe', 'true');
        localStorage.setItem('baas.rememberEmail', email);
        localStorage.setItem('baas.rememberPassword', password);
      } else {
        localStorage.removeItem('baas.rememberMe');
        localStorage.removeItem('baas.rememberEmail');
        localStorage.removeItem('baas.rememberPassword');
      }

      setAuthGuard(false);

      const { data: mfaData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (mfaData?.nextLevel === 'aal2' && mfaData?.currentLevel !== 'aal2') {
        navigate('/baas/mfa-verify');
      } else {
        const { data: factors } = await supabase.auth.mfa.listFactors();
        if ((factors?.totp || []).length === 0) navigate('/baas/mfa-setup');
        else navigate('/baas');
      }
    } catch {
      setAuthGuard(false);
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const parsed = z.string().trim().email().safeParse(email);
    if (!parsed.success) { setError('Please enter a valid email address.'); return; }

    setIsSendingReset(true);
    try {
      const redirectTo = `${window.location.origin}/baas/set-password`;
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({ email: parsed.data, redirectTo }),
      }).catch(() => null);

      if (res) {
        const data = await res.json().catch(() => ({}));
        setAlreadySent(!!data?.already_sent);
      }
      setView('forgot-sent');
    } catch {
      setError('Could not send reset email. Please try again.');
    } finally {
      setIsSendingReset(false);
    }
  };

  if (view === 'forgot' || view === 'forgot-sent') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-6">
          <div className="text-center space-y-2">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-accent/10 mb-2">
              <Banknote className="w-7 h-7 text-accent" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">
              {view === 'forgot-sent' ? 'Check your email' : 'Reset your password'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {view === 'forgot-sent'
                ? alreadySent
                  ? 'A reset link was already sent recently. Please check your inbox and spam folder.'
                  : `We sent a password reset link to ${email}.`
                : 'Enter your email and we\'ll send you a reset link.'}
            </p>
          </div>

          <Card className="border-border/50 bg-card/80 backdrop-blur-sm">
            <CardContent className="p-6">
              {view === 'forgot-sent' ? (
                <div className="space-y-4 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-accent/10 border border-accent/20 flex items-center justify-center mx-auto">
                    <Mail className="w-7 h-7 text-accent" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Click the link in your email to set a new password. The link expires in 1 hour. Check your spam folder if you don't see it.
                  </p>
                  <Button
                    variant="ghost"
                    className="w-full text-sm"
                    disabled={isSendingReset}
                    onClick={() => { setView('forgot'); setAlreadySent(false); }}
                  >
                    Didn't receive it? Try again
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  {error && (
                    <Alert variant="destructive" className="border-destructive/30 bg-destructive/10">
                      <AlertDescription className="text-sm">{error}</AlertDescription>
                    </Alert>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="reset-email" className="text-sm font-medium">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input id="reset-email" type="email" placeholder="you@company.com" value={email}
                        onChange={e => setEmail(e.target.value)} className="pl-10 bg-input/50 border-border/50"
                        required autoComplete="email" />
                    </div>
                  </div>
                  <Button type="submit" className="w-full" disabled={isSendingReset}>
                    {isSendingReset ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                    Send Reset Link
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>

          <button
            onClick={() => { setView('login'); setError(null); }}
            className="flex items-center gap-1 mx-auto text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-3 h-3" /> Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-accent/10 mb-2">
            <Banknote className="w-7 h-7 text-accent" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">BaaS Partner Portal</h1>
          <p className="text-sm text-muted-foreground">Sign in to manage your customers and IBANs</p>
        </div>

        <Card className="border-border/50 bg-card/80 backdrop-blur-sm">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive" className="border-destructive/30 bg-destructive/10">
                  <AlertDescription className="text-sm">{error}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="email" type="email" placeholder="you@company.com" value={email}
                    onChange={e => setEmail(e.target.value)} className="pl-10 bg-input/50 border-border/50"
                    required autoComplete="email" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                  <button
                    type="button"
                    onClick={() => { setView('forgot'); setError(null); }}
                    className="text-xs text-accent hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="••••••••"
                    value={password} onChange={e => setPassword(e.target.value)}
                    className="pl-10 pr-10 bg-input/50 border-border/50" required autoComplete="current-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input id="remember" type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} className="rounded border-border/50" />
                <Label htmlFor="remember" className="text-sm text-muted-foreground cursor-pointer">Remember me</Label>
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
                Sign In
              </Button>
            </form>
          </CardContent>
        </Card>
        <p className="text-center text-xs text-muted-foreground">
          Authorized BaaS Partners only. Contact your relationship manager for access.
        </p>
      </div>
    </div>
  );
}
